import axios from 'axios';
import { APIResponse, LoginAuth, Payout, SearchFilter } from './DashboardClasses';
import moment from 'moment';

const ROOT_URL = process.env.REACT_APP_API_URL+'/admin'
const DEBUG = process.env.REACT_APP_DEBUG=='1';
const PAGE_AUTH = process.env.REACT_APP_AUTH_URL;
export const CDN_URL = process.env.REACT_APP_CDN_URL;

interface PostOptions{
	token?: string,
}
async function post<T=any>(endpoint: string, data?: any, opts?: PostOptions) : Promise<APIResponse<T>>{
	var options : any = {
		headers: {
			'Content-Type': 'application/json',
		}
	}
	if(data instanceof FormData){
		options.headers['Content-Type'] = 'multipart/form-data';
	}
	if(opts?.token){
		options.headers["token"] = opts.token;
	}else{
		var auth = getLoginAuth();
		if(auth && auth.expiration>moment().unix()){
			options.headers["token"] = auth.token;
		}
	}

	var live = true;
	var live_mode = localStorage.getItem('live-mode');
	live = live_mode!=='0';
	options.headers["live"] = live ? 1 : 0;

	try{
		var ENDPOINT = `${ROOT_URL}/${endpoint}`;
		if(DEBUG) console.log("POST "+ENDPOINT, options);
		var res = await axios.post(ENDPOINT, (data || {}), options);
		if(DEBUG) console.log(` => Response: ${(res.headers['content-length']/1024).toFixed(2)} KB`);

		if(res && res.data && res.data.code){
			if(res.data.code==='P-TKNV'){
				if(!opts?.token){
					logout()
				}
			}
		}

		return res.data as APIResponse<T>;
	}catch(err){
		if(DEBUG) console.error(err);
		return {
			error: true, 
			message: 'Hubo un error inesperado (APIP-LCL-1)',
			code: 'APIP-LCL-1',
		}
	}
};

var CACHE : { [x: string]: APIResponse<any> } = {};
var cachePost = async <T=any>(endpoint: string, key: string, data?: any) : Promise<APIResponse<T>>=>{
	if(CACHE[key]) return CACHE[key] as APIResponse<T>;
	var res = await post(endpoint, data);
	if(!res.error) CACHE[key] = res;
	return res;
}

var scrollTop = ()=>{
	var contents = document.getElementById('siteContents')
	if(contents) contents.scrollTo(0,0)
}

var getLoginUrl = ()=>{
	return `${PAGE_AUTH}?c=${encodeURIComponent(`${window.location.origin}/auth`)}&r=${encodeURIComponent(window.location.href)}`
}

var getLoginAuth = ()=>{
	try{
		var auth_s = localStorage.getItem('auth');
		if(!auth_s) return null;
		var auth = JSON.parse(auth_s) as LoginAuth;
		return auth;
	}catch(e){
		return null;
	}
}

var getActiveClient = ()=>{
	try{
		var act = localStorage.getItem('active-client');
		if(!act) return null;
		var actp = parseInt(act);
		return Number.isNaN(actp) ? null : actp;
	}catch(e){
		return null;
	}
}

var logout = ()=>{
	localStorage.removeItem('auth');
	window.dispatchEvent(new Event('PYF-USER-AUTH'));
	window.location.href = getLoginUrl();
}

var verifyLogin = async (token: string, expiration: number)=>{
	var now = moment().unix();
	if(expiration<now) return null;

	try{
		var res = await post<{
			user_id: number,
			first_name: string,
			last_name: string,
			email: string,
			admin: boolean
		}>('user/me', null, { token });
		if(res.error) return null;
	}catch(e){
		return null;
	}

	try{
		var auth : LoginAuth = {
			user_id: res.data.user_id,
			first_name: res.data.first_name,
			last_name: res.data.last_name,
			email: res.data.email,
			admin: !!res.data.admin,
			token,
			expiration,
		}
		localStorage.setItem('auth', JSON.stringify(auth));
	}catch(e){
		return null;
	}

	try{
		window.dispatchEvent(new Event('PYF-USER-AUTH'));
	}catch(e){}

	return auth;
}

var getUserClients = ()=>{
	return cachePost('user/clients', 'USRCLLST');
}

var listPayments = (client_id: number, offset: number=0, count: number=30, cache: boolean=false)=>{
	if(cache){
		return cachePost('payment/list', `PYLST-${client_id}-${offset}`, { client_id, offset, count });
	}else{
		return post('payment/list', { client_id, offset, count });
	}
}

var getPayment = (payment_id: number)=>{
	return post('payment/get', { payment_id });
}

var getPaymentRefunds = (payment_id: number)=>{
	return post('payment/refunds/list', { payment_id });
}

var getPaymentEvents = (payment_id: number)=>{
	return post('payment/events/list', { payment_id });
}

var getPaymentService = (payment_id: number)=>{
	return post('payment/service/list', { payment_id });
}

var getRefund = (refund_id: number)=>{
	return post('payment/refunds/get', { refund_id });
}

var searchPayments = (client_id: number, query: string, filters: SearchFilter[], offset: number=0, count: number=30)=>{
	return post('payment/search', { client_id, offset, count, query, filters });
}

var listPayouts = (client_id: number, pending: boolean=true, completed: boolean=true, balance: boolean=false, offset: number=0, count: number=30)=>{
	return post<{
		pending: Payout[],
		completed: Payout[],
	}>('payout/list', { client_id, pending, completed, balance, offset, count });
}

var getPayout = (payout_id: number, payments: boolean=false, refunds: boolean=false, releases: boolean=false)=>{
	return post('payout/get', { payout_id, payments, refunds, releases });
}

var completePayout = (payout_id: number)=>{
	return post('payout/complete', { payout_id });
}

var getClientWebhooks = (client_id: number)=>{
	return post('client/webhooks/list', { client_id });
}

var getWebhook = (webhook_id: number)=>{
	return post('client/webhooks/get', { webhook_id });
}

var getWebhookLog = (webhook_id: number, offset: number=0, count: number=20)=>{
	return post('client/webhooks/logs', { webhook_id, offset, count });
}

var getWebhookResponse = (response_id: number)=>{
	return post('client/webhooks/response/get', { response_id });
}

var resendWebhook = (response_id: number)=>{
	return post<{ resent: boolean }>('client/webhooks/response/resend', { response_id });
}

var getUserList = (client_id: number)=>{
	return post('client/users/list', { client_id });
}

var searchUser = (email: string)=>{
	return post<{
		user_id: number,
		first_name: string,
		last_name: string,
		email: string,
	}>('client/users/search', { email });
}

var getUserClientAccess = (client_id: number)=>{
	return cachePost('client/access', `CLACC-${client_id}`, { client_id });
}

var setUserAccess = (client_id: number, user_id: number, access: number[])=>{
	return post('client/users/access', { client_id, user_id, access });
}

export default {
	completePayout,
	getActiveClient,
	getClientWebhooks,
	getLoginAuth,
	getLoginUrl,
	getPayment,
	getPaymentEvents,
	getPaymentRefunds,
	getPaymentService,
	getPayout,
	getRefund,
	getUserClientAccess,
	getUserClients,
	getUserList,
	getWebhook,
	getWebhookLog,
	getWebhookResponse,
	listPayments,
	listPayouts,
	logout,
	resendWebhook,
	scrollTop,
	searchPayments,
	searchUser,
	setUserAccess,
	verifyLogin,
}